import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  COLLAPSE, DURATION, EASE_OUT, IconButton, Icons, RADIUS, SHELL, SPRING_PANEL, SPRING_SNAPPY,
  Avatar, Segmented, WorkflowMark, cn,
} from '@workflow/ui'
import { Link } from '@/app/Link'
import { navigate, useLocation, type Route } from '@/app/router'
import { setAppearance, useAppearance } from '@/features/appearance/appearance'
import { useSession, useSignOut } from '@/lib/api/hooks'
import { personName } from '@/lib/people'
import { ROLE_LABEL } from '@/features/people/roles'
import { LevelGlyph } from '@/features/levels/LevelMark'
import { settingsGroupsFor } from '@/features/settings/sections'

/**
 * The standing destinations of the platform. The other surfaces of the plan —
 * the player portal and the public forms — are not destinations here: they
 * are different chrome on the same product, reached by the people who use
 * them, not by staff clicking around.
 */
const NAV: readonly { route: Route; label: string; icon: typeof Icons.operations }[] = [
  { route: { name: 'home' }, label: 'Home', icon: Icons.operations },
  { route: { name: 'announcements' }, label: 'Announcements', icon: Icons.announce },
  { route: { name: 'jobs' }, label: 'Jobs', icon: Icons.tasks },
  { route: { name: 'clients' }, label: 'Clients', icon: Icons.providers },
  { route: { name: 'people' }, label: 'People', icon: Icons.members },
  { route: { name: 'payments' }, label: 'Payments', icon: Icons.payments },
]

/** A player's destinations: the same column, their own rooms. */
const PLAYER_NAV: typeof NAV = [
  { route: { name: 'home' }, label: 'Home', icon: Icons.operations },
  { route: { name: 'announcements' }, label: 'Announcements', icon: Icons.announce },
  { route: { name: 'work' }, label: 'Work', icon: Icons.tasks },
  { route: { name: 'payments' }, label: 'Payments', icon: Icons.payments },
]

/**
 * The left column, on the console's mechanism.
 *
 * ---- One state, not two ---------------------------------------------------
 *
 * Open, or gone. There is no icon rail. A rail reads as a compromise that
 * keeps navigation reachable, and it is really a third thing to design and a
 * permanent tax on the window. Closed, the window is the work and nothing
 * else. The toggle waits in the strip over the page, and Ctrl B brings the
 * column back.
 *
 * ---- Why the inner column is a fixed width -------------------------------
 *
 * The `aside` animates its width; the column inside keeps its own. Without
 * that, every row would reflow on each frame of the animation. Fixed inside
 * a clipping parent means the column is simply revealed, and nothing inside
 * it is asked to re-measure.
 *
 * ---- Two layouts, one component -------------------------------------------
 *
 * Wide, the column is part of the row and animates its width, so the page is
 * genuinely narrower while it is open. Narrow, the column leaves the flow
 * entirely and slides over the page as a drawer under a scrim.
 *
 * ---- One column, one list --------------------------------------------------
 *
 * While Settings is the route, the column shows the settings tree instead of
 * the platform destinations — same width, same ground, same brand band —
 * because there is never more than one list of destinations on screen. The
 * way back rides the brand band's right end, on the same line as the
 * wordmark.
 */
export function Sidebar({
  open,
  compact,
  onToggle,
  onNavigate,
}: {
  /** Whether the column is on screen. Handed in by the shell; the shell owns the flag. */
  open: boolean
  /** Whether the column floats over the page rather than taking space from it. */
  compact: boolean
  onToggle: () => void
  /** Called when a row is followed, so the drawer can stand down over the destination. */
  onNavigate?: () => void
}) {
  const { route } = useLocation()
  const role = useSession().data?.account?.role
  const isAdmin = role === 'admin'
  const isPlayer = role === 'player'
  const inSettings = route.name === 'settings'

  /**
   * A closed column changing layout mode does not move. The two modes hold
   * a closed column differently — floating, it is full width and pushed off
   * screen; resident, it is in the row at no width — and the motion between
   * them, played on a column nobody can see, was not invisible. Only an
   * open-or-close is a motion; a mode change while closed is a jump.
   */
  const wasOpen = useRef(open)
  const changingWhileClosed = !open && !wasOpen.current
  useEffect(() => {
    wasOpen.current = open
  }, [open])

  return (
    <>
      {/* Narrow windows get a scrim, because a floating column over live content
          has to say the content behind it is not the thing being used. It is
          also the dismissal target. */}
      <AnimatePresence>
        {compact && open ? (
          <motion.button
            type="button"
            aria-label="Close sidebar"
            onClick={onToggle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DURATION.base, ease: EASE_OUT }}
            className="wf-scrim absolute inset-0 z-20"
          />
        ) : null}
      </AnimatePresence>

      <motion.aside
        animate={
          compact
            ? { x: open ? 0 : -SHELL.sidebarWidth, width: SHELL.sidebarWidth }
            : { x: 0, width: open ? SHELL.sidebarWidth : 0 }
        }
        transition={changingWhileClosed
          ? { duration: 0 }
          : compact
            ? SPRING_PANEL
            : { type: 'tween', duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
        // Two different objects, so two different edges. Wide, the column is
        // the ground the window is built on — square, no edge of its own; the
        // page is the object sitting on it, and the page carries the radius.
        // Floating, the roles invert: the column is the object resting on the
        // page, and an object carries the design language's own rounding on
        // the edge it is rested against.
        style={{
          ...(compact
            ? {
                borderTopRightRadius: RADIUS.xl,
                borderBottomRightRadius: RADIUS.xl,
              }
            : {}),
        }}
        className={cn(
          'h-full overflow-hidden bg-sidebar',
          compact ? 'absolute inset-y-0 left-0 z-30 shadow-panel' : 'relative z-20 shrink-0',
        )}
      >
        {/* `inert` while closed. A hidden column that still takes Tab is worse
            than a visible one: focus disappears into a panel nobody can see. */}
        <div inert={!open} className="flex h-full flex-col" style={{ width: SHELL.sidebarWidth }}>
          <BrandBand showBack={inSettings} />

          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={inSettings ? 'settings' : 'main'}
                initial={{ opacity: 0, x: inSettings ? 8 : -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: inSettings ? -8 : 8 }}
                transition={{ duration: DURATION.base, ease: EASE_OUT }}
              >
              {inSettings ? (
              // The tree begins where the nav list begins, and its rows are the
              // list's rows: a column that swaps its content in place should
              // not jump because of the swap.
              <SettingsTree
                active={route.name === 'settings' ? route.section : ''}
                onSelect={(section) => {
                  navigate({ name: 'settings', section })
                  onNavigate?.()
                }}
              />
            ) : (
              <nav aria-label="Main" className="flex flex-col gap-0.5 pt-1">
                {(isPlayer ? PLAYER_NAV : NAV).map((item, index) => ({ ...item, number: String(index + 1).padStart(2, '0') }))
                  .filter((item) => isPlayer || item.route.name !== 'payments' || isAdmin)
                  .map((item) => {
                  // A destination is selected only on its own page.
                  const selected = route.name === item.route.name || (item.route.name === 'payments' && route.name === 'payment-sheet')
                  return (
                    <NavRow
                      key={item.label}
                      to={item.route}
                      number={item.number}
                      label={item.label}
                      selected={selected}
                      onNavigate={onNavigate}
                    />
                  )
                })}
              </nav>
              )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* While the tree is what this column holds, the footer's settings
              shortcut would be a second copy of where the column already is. */}
          <AnimatePresence initial={false}>
            {!inSettings ? (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: DURATION.base, ease: EASE_OUT }}
              >
                <Footer onNavigate={onNavigate} />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.aside>
    </>
  )
}

/**
 * The brand: mark and wordmark, occupying the strip's own band. The height is
 * the strip's height, not padding chosen by eye — the strip only spans the
 * page, so sharing the one number puts the wordmark and the strip's toggle on
 * a single line, and keeps them there if the strip is ever retuned.
 */
function BrandBand({ showBack }: { showBack: boolean }) {
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-1.5 px-3.5 select-none"
      style={{ height: SHELL.topStripHeight }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <WorkflowMark size={16} className="pointer-events-none text-foreground" />
        <span className="pointer-events-none font-mono text-[15px] lowercase text-foreground select-none">workflow</span>
      </div>

      {showBack ? (
        <IconButton label="Back to Home" tooltipSide="bottom" onClick={() => navigate({ name: 'home' })}>
          <Icons.back size="md" />
        </IconButton>
      ) : (
        <span aria-hidden className="font-mono text-[10px] tracking-[0.1em] text-muted-foreground/60 uppercase select-none">
          Index
        </span>
      )}
    </div>
  )
}

/**
 * The selection, drawn the way an index draws it: a rule beside the row,
 * never a chip behind it. One `layoutId` per list, so following a
 * destination reads as the rule travelling down the column while the page
 * crossfades beside it — travel, not a swap. Hover never adds a background
 * either; the text alone inks. Backgrounds never change in this column.
 */
function IndexRule({ list }: { list: string }) {
  return (
    <motion.span
      layoutId={`${list}-rule`}
      transition={SPRING_SNAPPY}
      aria-hidden
      className="ml-auto h-[2px] w-[18px] shrink-0 self-center bg-foreground"
    />
  )
}

/**
 * One destination: its number, its name, and the rule when selected. Rows
 * are divided by hairlines, so the column reads as contents, not controls.
 */
function NavRow({
  to,
  number,
  label,
  selected,
  onNavigate,
}: {
  to: Route
  number: string
  label: string
  selected: boolean
  onNavigate?: () => void
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={selected ? 'page' : undefined}
      className={cn(
        'flex w-full items-baseline gap-2.5 border-b border-hairline px-2 py-[7px] text-left transition-colors duration-fast last:border-b-0',
        selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <span aria-hidden className={cn('font-mono text-[11px]', selected ? 'text-foreground' : 'text-muted-foreground/60')}>
        {number}
      </span>
      <span className={cn('min-w-0 flex-1 truncate font-mono text-xs tracking-[0.06em] uppercase', selected && 'font-medium')}>
        {label}
      </span>
      {selected ? <IndexRule list="sidebar" /> : null}
    </Link>
  )
}

/** Settings' sections, as the sidebar column's content while Settings is the route. */
function SettingsTree({
  active,
  onSelect,
}: {
  active: string
  onSelect: (section: string) => void
}) {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  return (
    <nav aria-label="Settings sections" className="flex flex-col gap-0.5 pt-1">
      {settingsGroupsFor(session.data?.account?.role).map((group) => {
        // The Studio group is the administrators' machinery; the tree only
        // names a section to those who may open it.
        const sections = group.sections.filter((section) => section.id !== 'api' || isAdmin)
        if (sections.length === 0) return null
        return (
        <div key={group.title} className="mt-2 first:mt-0">
          <p className="text-micro px-2 pb-1 text-muted-foreground/70 uppercase">{group.title}</p>
          {sections.map((section) => {
            const selected = section.id === active
            return (
              <button
                key={section.id}
                type="button"
                aria-current={selected ? 'page' : undefined}
                onClick={() => onSelect(section.id)}
                className={cn(
                  'flex w-full items-baseline gap-2.5 px-2 py-[7px] text-left transition-colors duration-fast',
                  selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <span className={cn('min-w-0 flex-1 truncate font-mono text-xs tracking-[0.06em] uppercase', selected && 'font-medium')}>
                  {section.name}
                </span>
                {selected ? <IndexRule list="settings-tree" /> : null}
              </button>
            )
          })}
        </div>
        )
      })}
    </nav>
  )
}

/**
 * No rule above it and none below the column's rows. The account row is the
 * footer: who the console assumes, and the door to the person's own things —
 * settings, the theme, leaving.
 */
function Footer({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="shrink-0 px-2 pt-2 pb-2">
      <AccountMenu onNavigate={onNavigate} />
    </div>
  )
}

/**
 * The account row and what belongs to the person: the theme and the way
 * out, unfolding in place beneath the row. No floating card, no shadow, no
 * dividers — the expanded block is spacing and rows, part of the column.
 */
function AccountMenu({ onNavigate }: { onNavigate?: () => void }) {
  const session = useSession()
  const signOut = useSignOut()
  const appearance = useAppearance()
  const account = session.data?.account
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const go = (route: Route) => {
    setOpen(false)
    navigate(route)
    onNavigate?.()
  }

  return (
    <div ref={rootRef}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="group flex w-full items-center gap-2.5 px-2 py-2 text-left transition-colors duration-fast"
      >
        <Avatar name={account ? personName(account) : null} email={account?.email ?? ''} src={account?.pictureUrl} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-xs tracking-[0.04em] text-foreground">
            {account ? personName(account) : '…'}
          </span>
          {/* The role, and for a player the level they stand on beside it — its mark and its name. */}
          <span className="flex min-w-0 items-center gap-1.5 font-mono text-[10px] tracking-[0.1em] text-muted-foreground/60 uppercase transition-colors duration-fast group-hover:text-muted-foreground">
            <span className="shrink-0">{account ? ROLE_LABEL[account.role] : ''}</span>
            {account?.level ? (
              <>
                <span aria-hidden>·</span>
                <span className="flex min-w-0 items-center gap-1 text-foreground/70" title={account.level.name}>
                  <LevelGlyph icon={account.level.icon} className="size-3 shrink-0" />
                  <span className="truncate">{account.level.name}</span>
                </span>
              </>
            ) : null}
          </span>
        </span>
        {open ? <IndexRule list="sidebar-account" /> : null}
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            variants={COLLAPSE}
            initial="initial"
            animate="animate"
            exit="exit"
            className="overflow-hidden"
          >
            <div className="px-2 pt-1 pb-2">
              {/* The theme as three glyphs in the column's own air — no box,
                  no edge, the chosen glyph inked and the rest quiet. */}
              <Segmented
                label="Theme"
                value={appearance.theme}
                onChange={(theme) => setAppearance({ theme })}
                fullWidth
                size="sm"
                hideLabels
                variant="plain"
                options={[
                  { value: 'system', label: 'System', icon: <Icons.themeSystem size="sm" /> },
                  { value: 'light', label: 'Light', icon: <Icons.themeLight size="sm" /> },
                  { value: 'dark', label: 'Dark', icon: <Icons.themeDark size="sm" /> },
                ]}
              />
              <div className="mt-1 flex items-center justify-between" role="group" aria-label="Account actions">
                <IconButton label="Settings" tooltipSide="top" onClick={() => go({ name: 'settings', section: 'account' })}>
                  <Icons.settings />
                </IconButton>
                {/* Leaving is danger-coloured: the one destructive thing here
                    should read as such before it is ever touched. */}
                <button
                  type="button"
                  disabled={signOut.isPending}
                  onClick={() => {
                    setOpen(false)
                    signOut.mutate(undefined)
                  }}
                  className="px-2 py-[7px] text-left font-mono text-xs tracking-[0.06em] text-danger/80 uppercase transition-colors duration-fast hover:text-danger disabled:pointer-events-none disabled:opacity-45"
                >
                  {signOut.isPending ? 'Signing out…' : 'Sign out'}
                </button>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}


